export function ComingSoonBadge({ children = 'Prochainement' }) {
  return (
    <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-primary-700">
      {children}
    </span>
  );
}

export function SettingsPageHeader({
  title,
  subtitle,
  tabs,
  activeTab,
  onTabChange,
  kicker = null,
  badge = null,
  identity = null,
  leadingIcon: LeadingIcon = null,
}) {
  return (
    <div className="mb-8">
      {(kicker || badge) ? (
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {kicker ? (
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-700">
            {kicker}
          </span>
        ) : null}
        {badge ? (
          <span className="inline-flex items-center rounded-full bg-primary-600 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-white">
            {badge}
          </span>
        ) : null}
      </div>
      ) : null}
      <div className="flex items-start gap-3">
        {LeadingIcon ? (
          <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
            <LeadingIcon className="h-5 w-5" />
          </div>
        ) : null}
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold tracking-tight text-gray-900">{title}</h1>
          {identity ? (
            <p className="mt-1 text-sm font-semibold text-gray-800">{identity}</p>
          ) : null}
          {subtitle ? (
            <p className="mt-1.5 max-w-2xl text-[15px] leading-6 text-gray-500">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {tabs?.length ? (
        <nav className="mt-6 flex flex-wrap gap-1 border-b border-gray-200" aria-label="Sections des paramètres">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                aria-current={active ? 'page' : undefined}
                className={`relative -mb-px inline-flex items-center gap-2 px-3 pb-3 pt-1 text-sm font-medium transition-colors ${
                  active
                    ? 'text-primary-700'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {Icon ? <Icon className={`h-4 w-4 ${active ? 'text-primary-600' : 'text-gray-400'}`} /> : null}
                {tab.label}
                {active ? (
                  <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary-600" />
                ) : null}
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}

export function SettingsCard({ icon: Icon, title, subtitle, children, className = '', footer = null }) {
  return (
    <section className={`rounded-2xl border border-gray-200/80 bg-white p-6 shadow-[0_8px_30px_rgba(15,23,42,0.04)] ${className}`}>
      <div className="mb-5 flex items-start gap-3">
        {Icon ? (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle ? <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p> : null}
        </div>
      </div>
      {children}
      {footer}
    </section>
  );
}

export function SettingsSwitch({ checked, onChange, disabled = false, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange(!checked);
      }}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition ${
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export function SettingsLavenderGroup({ title, children }) {
  return (
    <div>
      <div className="mb-3 rounded-lg bg-[#eeeaf6] px-3 py-1.5">
        <p className="text-sm font-semibold text-primary-800">{title}</p>
      </div>
      {children}
    </div>
  );
}

export function SettingsTextField({
  label,
  value,
  onChange,
  compact = false,
  autoComplete = 'off',
  type = 'text',
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-gray-600">{label}</span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-xl border border-gray-200 bg-white px-3.5 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 ${compact ? 'py-2' : 'py-2.5'}`}
      />
    </label>
  );
}

export function SettingsReadOnlyField({ label, value, locked = false, lockIcon: LockIcon, emptyLabel = 'Non renseigné', compact = false }) {
  const display = value && String(value).trim() ? value : emptyLabel;
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-gray-600">{label}</span>
      <div className="relative">
        <div className={`w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-900 ${compact ? 'py-2' : 'py-2.5'} ${locked ? 'pr-10' : ''}`}>
          {display}
        </div>
        {locked && LockIcon ? (
          <LockIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        ) : null}
      </div>
    </label>
  );
}

export function SettingsComingSoonRow({ icon: Icon, title, description }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-3">
      <div className="flex min-w-0 items-center gap-3">
        {Icon ? (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400">
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          {description ? <p className="text-xs text-gray-500">{description}</p> : null}
        </div>
      </div>
      <ComingSoonBadge />
    </div>
  );
}

export function SettingsVolumeSlider({ value, onChange, disabled = false }) {
  const percent = Math.round((Number(value) || 0) * 100);
  return (
    <div className="space-y-2">
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="settings-volume-slider w-full"
        aria-label="Volume"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      />
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>0 %</span>
        <span className="font-medium text-gray-700">{percent} %</span>
        <span>100 %</span>
      </div>
    </div>
  );
}
